import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { List, Map as MapIcon, Route as RouteIcon, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ListView } from "@/features/process-map/ListView";
import { MapView, type ScenarioState } from "@/features/process-map/MapView";
import { ScenariosView } from "@/features/process-map/ScenariosView";
import { StageDetail } from "@/features/process-map/StageDetail";
import { toneVar } from "@/features/process-map/meta";
import { workCountsQuery } from "@/features/process-map/work";
import { loadTexts } from "@/i18n";
import { useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { findSession } from "@/navigation";
import { matchStages, parseNodeKey, type NodeKey } from "@/process/processGraph";
import { processes, processIds, processTones, scenarioIds, scenarios, type ProcessId, type ScenarioId } from "@/process/processes";

// Process map (PX-SYS-230, /process-map, under Home in the sidebar). Three
// views over the same data (src/process/processes.ts):
//   Map        every process as a swimlane, steps as cards, hand-offs as
//              arrows; hover / select a step to see where it comes from and
//              leads; a scenario player walks one business case step by step.
//   Scenarios  the business cases as cards, each started on the map.
//   List       every step as a table row, grouped by process.
// Search ("q") filters / highlights steps by their texts and screens. The
// work waiting at each step comes from one RPC (process_work_counts, ≤ 100
// per count); everything else is static — zero round-trips.

type View = "map" | "scenarios" | "list";
const views: View[] = ["map", "scenarios", "list"];

interface Search {
  view?: View | undefined;
  stage?: string | undefined;
  process?: ProcessId | undefined;
  q?: string | undefined;
  scenario?: ScenarioId | undefined;
  step?: number | undefined;
}

export const Route = createFileRoute("/_app/process-map")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {};
    if (typeof raw.view === "string" && (views as string[]).includes(raw.view) && raw.view !== "map") s.view = raw.view as View;
    if (typeof raw.stage === "string" && parseNodeKey(raw.stage)) s.stage = raw.stage;
    if (typeof raw.process === "string" && (processIds as readonly string[]).includes(raw.process)) s.process = raw.process as ProcessId;
    if (typeof raw.q === "string" && raw.q.trim()) s.q = raw.q.slice(0, 60);
    if (typeof raw.scenario === "string" && (scenarioIds as readonly string[]).includes(raw.scenario)) {
      s.scenario = raw.scenario as ScenarioId;
      const step = Number(raw.step ?? 0);
      s.step = Number.isInteger(step) && step >= 0 ? Math.min(step, scenarios[s.scenario].length - 1) : 0;
    }
    return s;
  },
  component: ProcessMapPage,
});

function ProcessMapPage() {
  const { t, i18n } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { company } = useActiveCompany();
  const counts = useQuery(workCountsQuery(company?.id ?? "")).data;
  const [showLinks, setShowLinks] = useState(false);
  const view: View = search.view ?? "map";
  // The search covers both languages: load the other one's texts (a small chunk).
  const [textsReady, setTextsReady] = useState(0);
  useEffect(() => {
    let live = true;
    void loadTexts("pl").then(() => live && setTextsReady((n) => n + 1));
    return () => {
      live = false;
    };
  }, []);

  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev: Search) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const selected = search.stage ? parseNodeKey(search.stage) : null;
  const select = (key: NodeKey | undefined) => setSearch({ stage: key && key === search.stage ? undefined : key });

  // Search over each step's texts in both languages (a Polish user may type
  // an English term and back): title, description, process, screens, codes.
  const textOf = useCallback(
    (process: ProcessId, stage: string) => {
      const s = processes[process].find((x) => x.key === stage);
      return (["en", "pl"] as const)
        .map((lng) => {
          const ft = i18n.getFixedT(lng) as unknown as (key: string) => string;
          const screens = (s?.sessions ?? []).map((code) => {
            const target = findSession(code);
            return `${code} ${target ? ft(target.item.labelKey) : ""}`;
          });
          return [
            ft(`processMap.stages.${stage}`),
            ft(`guide.processes.${process}.stages.${stage}`),
            ft(`guide.processes.${process}.title`),
            ...screens,
          ].join(" ");
        })
        .join(" ");
    },
    // textsReady: recompute once the other language's texts are in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n, textsReady],
  );
  const matches = useMemo(() => matchStages(search.q ?? "", textOf), [search.q, textOf]);

  const scenario: ScenarioState | null = search.scenario ? { id: search.scenario, step: search.step ?? 0 } : null;
  const lanes = search.process && !scenario ? [search.process] : processIds;

  const tab = (v: View, icon: ReactNode, label: string) => (
    <button
      key={v}
      type="button"
      role="tab"
      id={`pm-tab-${v}`}
      aria-selected={view === v}
      aria-controls={`pm-panel-${v}`}
      onClick={() => setSearch({ view: v === "map" ? undefined : v, stage: undefined })}
      className={cn(
        "flex h-8 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-focus",
        view === v ? "bg-surface text-ink shadow-sm" : "text-ink-muted hover:text-ink",
      )}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className={cn("flex min-h-0 grow flex-col gap-4 pb-8", selected && view !== "scenarios" && "sm:pr-[372px]")} data-testid="process-map">
      <header className="flex flex-wrap items-end gap-4">
        <div className="flex grow flex-col gap-1">
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("processMap.title")}</h1>
          <p className="max-w-3xl text-[14px] text-ink-soft">{t("processMap.introVisual")}</p>
        </div>
        <div role="tablist" aria-label={t("processMap.viewsLabel")} className="flex gap-0.5 rounded-xl bg-surface-muted p-1">
          {tab("map", <MapIcon size={14} aria-hidden="true" />, t("processMap.views.map"))}
          {tab("scenarios", <RouteIcon size={14} aria-hidden="true" />, t("processMap.views.scenarios"))}
          {tab("list", <List size={14} aria-hidden="true" />, t("processMap.views.list"))}
        </div>
      </header>

      <div role="tabpanel" id={`pm-panel-${view}`} aria-labelledby={`pm-tab-${view}`} className="flex flex-col gap-4">
        {view !== "scenarios" && (
          <Toolbar
            search={search}
            matches={matches}
            showLinks={view === "map" ? showLinks : null}
            onLinks={setShowLinks}
            setSearch={setSearch}
          />
        )}

        {view === "map" && (
          <MapView
            lanes={lanes}
            selected={search.stage as NodeKey | undefined}
            matches={matches}
            showLinks={showLinks}
            counts={counts}
            scenario={scenario}
            onSelect={select}
            onScenarioStep={(step) => setSearch({ step })}
            onScenarioExit={() => setSearch({ scenario: undefined, step: undefined })}
          />
        )}
        {view === "scenarios" && (
          <ScenariosView onStart={(id) => setSearch({ view: undefined, scenario: id, step: 0, stage: undefined, process: undefined, q: undefined })} />
        )}
        {view === "list" && <ListView lanes={lanes} matches={matches} counts={counts} onSelect={select} />}
        {matches && matches.size === 0 && view !== "scenarios" && (
          <p className="text-[13px] text-ink-muted">{t("processMap.searchNone", { q: search.q ?? "" })}</p>
        )}
      </div>

      {selected && view !== "scenarios" && (
        <aside
          aria-label={t("processMap.details")}
          className="fixed inset-x-3 bottom-3 z-40 max-h-[70vh] overflow-y-auto rounded-2xl border border-border bg-surface p-4 shadow-2xl sm:inset-x-auto sm:inset-y-3 sm:right-3 sm:max-h-none sm:w-[360px]"
          onKeyDown={(e) => {
            if (e.key === "Escape") select(undefined);
          }}
        >
          <StageDetail
            process={selected.process}
            stage={selected.stage}
            counts={counts}
            onSelect={(k) => select(k)}
            onClose={() => select(undefined)}
          />
        </aside>
      )}
    </div>
  );
}

function Toolbar({
  search,
  matches,
  showLinks,
  onLinks,
  setSearch,
}: {
  search: Search;
  matches: Set<NodeKey> | null;
  /** null: the view has no hand-off arrows. */
  showLinks: boolean | null;
  onLinks: (v: boolean) => void;
  setSearch: (patch: Partial<Search>) => void;
}) {
  const { t } = useTranslation();
  const tr = t as unknown as (key: string) => string;
  const [text, setText] = useState(search.q ?? "");
  useEffect(() => {
    if (text === (search.q ?? "")) return;
    const id = setTimeout(() => setSearch({ q: text.trim() ? text : undefined }), 180);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex h-9 min-w-[260px] grow items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus sm:max-w-sm">
          <Search size={15} aria-hidden="true" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && matches && matches.size > 0) setSearch({ stage: [...matches][0] });
              if (e.key === "Escape") setText("");
            }}
            placeholder={t("processMap.search")}
            aria-label={t("processMap.search")}
            className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
          />
          {matches && <span className="shrink-0 text-[12px] text-ink-muted">{t("processMap.searchCount", { count: matches.size })}</span>}
          {text && (
            <button type="button" aria-label={t("list.clearFilters")} onClick={() => setText("")} className="text-ink-muted hover:text-ink">
              <X size={14} />
            </button>
          )}
        </label>
        <span className="grow" />
        {showLinks !== null && !search.scenario && (
          <label className="flex items-center gap-2 text-[13px] text-ink-soft">
            <input type="checkbox" checked={showLinks} onChange={(e) => onLinks(e.target.checked)} className="size-4 accent-(--accent)" />
            {t("processMap.showHandoffs")}
          </label>
        )}
      </div>
      {!search.scenario && (
        <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label={t("processMap.filter")}>
          <button type="button" aria-pressed={!search.process} onClick={() => setSearch({ process: undefined })} className={chip(!search.process)}>
            {t("processMap.allProcesses")}
          </button>
          {processIds.map((id) => (
            <button
              key={id}
              type="button"
              aria-pressed={search.process === id}
              onClick={() => setSearch({ process: search.process === id ? undefined : id })}
              className={chip(search.process === id)}
            >
              <span aria-hidden="true" className="size-2 rounded-full" style={{ background: toneVar(processTones[id]) }} />
              {tr(`guide.processes.${id}.title`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const chip = (on: boolean) =>
  cn(
    "flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] transition-colors focus-visible:outline-2 focus-visible:outline-focus",
    on ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink-soft hover:bg-surface-hover hover:text-ink",
  );
